import { debounce, getSectionHTML } from '@theme/utilities';

class CollectionFilters extends HTMLElement {
  #filtersOpen = false;
  #sortOpen = false;
  #priceDebounce = debounce(() => this.#updateFilters(), 500);

  connectedCallback() {
    this.form = this.querySelector('[data-filter-form]');
    this.sidebar = this.querySelector('[data-filter-sidebar]');
    this.filterBarDesktop = this.querySelector('[data-filter-bar-desktop]');
    this.filterBarMobile = this.querySelector('[data-filter-bar-mobile]');
    this.filterToggle = this.querySelector('[data-filter-toggle]');
    this.filterToggleLabel = this.querySelector('[data-filter-toggle-label]');
    this.sortToggle = this.querySelector('[data-sort-toggle]');
    this.sortDropdown = this.querySelector('[data-sort-dropdown]');
    this.sortChevron = this.querySelector('[data-sort-chevron]');
    this.mobileDrawer = this.querySelector('[data-mobile-filter-drawer]');
    this.mobileSlot = this.querySelector('[data-mobile-filter-slot]');
    this.sectionId = this.closest('.shopify-section')?.id;

    this.filterToggle?.addEventListener('click', this.#handleFilterToggle);
    this.sortToggle?.addEventListener('click', this.#handleSortToggle);
    this.querySelector('[data-mobile-filter-open]')?.addEventListener('click', this.#openMobileDrawer);
    this.querySelectorAll('[data-mobile-filter-close]').forEach(
      (btn) => btn.addEventListener('click', this.#closeMobileDrawer)
    );
    this.mobileDrawer?.querySelector('[data-overlay]')?.addEventListener('click', this.#closeMobileDrawer);
    document.addEventListener('theme:escape', this.#closeMobileDrawer);

    this.addEventListener('change', this.#handleInputChange);
    this.addEventListener('input', this.#handlePriceInput);
    this.addEventListener('click', this.#handleChipClick);

    document.addEventListener('click', this.#handleOutsideClick);
    window.addEventListener('popstate', this.#handlePopState);

    this.form?.addEventListener('submit', (e) => e.preventDefault());

    const sentinel = this.querySelector('[data-scroll-sentinel]');
    if (sentinel) {
      this.#activateObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting && !this.#scrolled) {
          this.#scrolled = true;
          this.filterBarDesktop?.classList.add('active');
          if (this.filterBarDesktop) this.filterBarDesktop.style.height = '46px';
          this.filterBarMobile?.classList.add('active');
          if (this.filterBarMobile) this.filterBarMobile.style.height = '36px';
        }
      }, { rootMargin: '-85px 0px 0px 0px' });

      this.#deactivateObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && this.#scrolled) {
          this.#scrolled = false;
          this.filterBarDesktop?.classList.remove('active');
          if (this.filterBarDesktop) this.filterBarDesktop.style.height = '64px';
          this.filterBarMobile?.classList.remove('active');
          if (this.filterBarMobile) this.filterBarMobile.style.height = '50px';
        }
      }, { rootMargin: '-65px 0px 0px 0px' });

      this.#activateObserver.observe(sentinel);
      this.#deactivateObserver.observe(sentinel);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('theme:escape', this.#closeMobileDrawer);
    window.removeEventListener('popstate', this.#handlePopState);
    this.#activateObserver?.disconnect();
    this.#deactivateObserver?.disconnect();
    this.#priceDebounce.cancel();
  }

  #scrolled = false;
  #activateObserver = null;
  #deactivateObserver = null;

  #handleFilterToggle = () => {
    this.#filtersOpen = !this.#filtersOpen;

    if (this.#filtersOpen) {
      this.sidebar.classList.add('w-82', 'opacity-100', 'mr-4');
      this.sidebar.classList.remove('w-0', 'overflow-hidden', 'opacity-0');
      this.filterToggleLabel.textContent = 'Hide Filters';
    } else {
      this.sidebar.classList.add('w-0', 'overflow-hidden', 'opacity-0');
      this.sidebar.classList.remove('w-82', 'opacity-100', 'mr-4');
      this.filterToggleLabel.textContent = 'Show Filters';
    }
  };

  #handleSortToggle = (e) => {
    e.stopPropagation();
    this.#sortOpen = !this.#sortOpen;
    this.sortDropdown.classList.toggle('hidden', !this.#sortOpen);
    this.sortChevron?.classList.toggle('rotate-180', this.#sortOpen);
    if (this.#sortOpen) {
      const firstInput = this.sortDropdown.querySelector('input, button, a');
      if (firstInput) firstInput.focus();
    }
  };

  #handleOutsideClick = (e) => {
    if (this.#sortOpen && !e.target.closest('[data-sort-wrapper]')) {
      this.#sortOpen = false;
      this.sortDropdown?.classList.add('hidden');
      this.sortChevron?.classList.remove('rotate-180');
    }
  };

  #handleInputChange = (e) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-sort-radio]')) {
      const label = target.closest('label');
      if (label) {
        const sortLabel = this.querySelector('[data-sort-label]');
        if (sortLabel) sortLabel.textContent = label.textContent.trim();
      }
      this.#sortOpen = false;
      this.sortDropdown?.classList.add('hidden');
      this.sortChevron?.classList.remove('rotate-180');
    }

    if (target.matches('[data-filter-input]') || target.matches('[data-sort-radio]') || target.name === 'sort_by') {
      this.#updateFilters();
    }
  };

  #handlePriceInput = (e) => {
    if (e.target instanceof HTMLInputElement && e.target.matches('[data-filter-price]')) {
      this.#priceDebounce();
    }
  };

  #handleChipClick = (e) => {
    const removeLink = e.target.closest('[data-remove-filter]');
    const clearLink = e.target.closest('[data-clear-all]');
    const link = removeLink || clearLink;

    if (!link) return;

    e.preventDefault();
    const url = new URL(link.href);
    history.pushState({}, '', url.toString());
    this.#renderSection(url);
  };

  #handlePopState = () => {
    const url = new URL(window.location.href);
    this.#renderSection(url);
  };

  #mobileFilterTrigger = null;

  #openMobileDrawer = () => {
    this.#mobileFilterTrigger = document.activeElement;
    const panel = this.querySelector('[data-filter-panel]');
    if (panel) this.mobileSlot.appendChild(panel);

    this.mobileDrawer.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    const firstFocusable = this.mobileDrawer.querySelector('button, input, a, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) firstFocusable.focus();
  };

  #closeMobileDrawer = () => {
    if (!this.mobileDrawer?.hasAttribute('open')) return;

    this.mobileDrawer.removeAttribute('open');
    document.body.style.overflow = '';

    const panel = this.querySelector('[data-filter-panel]');
    const sidebarTarget = this.sidebar?.querySelector('div') || this.sidebar;
    if (panel && sidebarTarget) {
      sidebarTarget.appendChild(panel);
    }

    if (this.#mobileFilterTrigger) {
      this.#mobileFilterTrigger.focus();
      this.#mobileFilterTrigger = null;
    }
  };

  #createURLParameters() {
    const formData = new FormData(this.form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string' && value !== '') {
        params.append(key, value);
      }
    }

    const desktopSort = params.get('sort_by_desktop');
    if (desktopSort) {
      params.set('sort_by', desktopSort);
      params.delete('sort_by_desktop');
    }

    if (params.get('filter.v.price.gte') === '') params.delete('filter.v.price.gte');
    if (params.get('filter.v.price.lte') === '') params.delete('filter.v.price.lte');
    params.delete('page');

    return params;
  }

  #updateFilters() {
    const params = this.#createURLParameters();
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of params) {
      url.searchParams.append(key, value);
    }

    history.pushState({}, '', url.toString());
    this.#renderSection(url);
  }

  async #renderSection(url) {
    this.#setLoading(true);

    try {
      const html = await getSectionHTML(this.sectionId, url);
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      this.#replaceRegion(parsed, '[data-products-container]');
      this.#replaceRegion(parsed, '[data-active-filters-container]');
      this.#replaceRegion(parsed, '[data-product-count]');
      this.#replaceRegion(parsed, '[data-product-count-mobile]');
      const mobileOpen = this.mobileDrawer?.hasAttribute('open');
      const currentPanel = this.querySelector('[data-filter-panel]');
      const openDetails = currentPanel
        ? [...currentPanel.querySelectorAll('details')].map((d) => d.open)
        : [];

      this.#replaceRegion(parsed, '[data-filter-panel]');

      const newPanel = this.querySelector('[data-filter-panel]');
      if (newPanel) {
        newPanel.querySelectorAll('details').forEach((d, i) => {
          if (i < openDetails.length) d.open = openDetails[i];
        });
      }

      if (mobileOpen && newPanel) {
        this.mobileSlot.appendChild(newPanel);
      }

      this.#syncGridCols();
    } catch (error) {
      console.error('Failed to update filters:', error);
    } finally {
      this.#setLoading(false);
    }
  }

  #replaceRegion(parsed, selector) {
    const newEl = parsed.querySelector(selector);
    const existingEl = this.querySelector(selector);
    if (newEl && existingEl) {
      existingEl.replaceWith(newEl);
    }
  }

  #syncGridCols() {
  }

  #setLoading(loading) {
    const grid = this.querySelector('[data-product-grid]');
    if (grid) {
      grid.style.opacity = loading ? '0.5' : '';
      grid.style.pointerEvents = loading ? 'none' : '';
    }
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
